"use client";

import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";
import type { Locale } from "@/lib/i18n/config";
import {
	AD_FORMAT_EXPERIMENT_FLAG,
	useAdFormatArm,
} from "@/lib/monetization/ad-format-experiment";
import { useAdGuardrails } from "@/lib/monetization/use-ad-guardrails";
import { AD_BREAK_SURFACE, AdBreakCard } from "./ad-break-card";
import { APPLY_CLICKED_EVENT, clearPostApplyPending } from "./post-apply-offer";

const CONFIRMATION = {
	en: "Application sent",
	id: "Lamaran terkirim",
} as const;

// Any mounted instance hears the same click; count each job's exposure once
// per page life.
const exposedJobs = new Set<string>();

/** Test hook: the set outlives a test. */
export function resetApplyAdBreakExposure(): void {
	exposedJobs.clear();
}

/**
 * Post-apply success panel for the `ad_breaks` arm of `ad-format-experiment`.
 *
 * Reveals in-flow on the synchronous `ld:apply-clicked` event, exactly like
 * `AfterApplyInline`, so the panel is ALREADY rendered when the user tabs back
 * from the employer's site (`visibilitychange`). No new tab, popup, overlay or
 * redirect, and `/out` is untouched.
 *
 * Renders nothing in the `control` arm, for Pro, and until the flag resolves.
 * In `ad_breaks` it takes the post-apply slot (`clearPostApplyPending`, so the
 * tab-return `PostApplyOffer` popup self-suppresses) and `AfterApplyInline`
 * stands down, so an apply shows one paid unit. The paid first-party
 * `ApplyMomentCvOffer` keeps priority: when its flag is on the ad break is
 * skipped and `monetized_suppressed reason:"cv_offer_priority"` is logged.
 */
export function ApplyAdBreak({
	jobId,
	categoryId,
	locale,
}: {
	jobId: string;
	categoryId: string | null;
	locale: Locale;
}) {
	const posthog = usePostHog();
	const { ready, arm, adBreaks } = useAdFormatArm();
	// Pro (and a capped session) render no panel at all, not even the
	// confirmation: this arm adds ads, it never adds chrome for a viewer with none.
	const guard = useAdGuardrails(AD_BREAK_SURFACE);
	const cvOfferEnabled = useFeatureFlagEnabled("apply-moment-cv-offer");
	const [applied, setApplied] = useState(false);
	const suppressedLogged = useRef(false);

	// Always listen so a click that lands before the flag resolves is not missed;
	// the reveal below is what the arm gates.
	useEffect(() => {
		const onApply = (e: Event) => {
			const detail = (e as CustomEvent<{ jobId?: string }>).detail;
			if (detail?.jobId === jobId) setApplied(true);
		};
		window.addEventListener(APPLY_CLICKED_EVENT, onApply);
		return () => window.removeEventListener(APPLY_CLICKED_EVENT, onApply);
	}, [jobId]);

	const cvOfferWins = cvOfferEnabled === true;
	const show =
		applied &&
		ready &&
		adBreaks &&
		!cvOfferWins &&
		guard.ready &&
		!guard.blocked;

	// Exposure at the apply moment, both arms, so the experiment compares the
	// people who actually reached it.
	useEffect(() => {
		if (!applied || !ready || exposedJobs.has(jobId)) return;
		exposedJobs.add(jobId);
		posthog?.capture("experiment_exposure", {
			experiment: AD_FORMAT_EXPERIMENT_FLAG,
			variant: arm,
		});
	}, [applied, ready, arm, jobId, posthog]);

	useEffect(() => {
		if (!applied || !ready || !adBreaks || !cvOfferWins) return;
		if (suppressedLogged.current) return;
		suppressedLogged.current = true;
		posthog?.capture("monetized_suppressed", {
			surface: AD_BREAK_SURFACE,
			reason: "cv_offer_priority",
		});
	}, [applied, ready, adBreaks, cvOfferWins, posthog]);

	useEffect(() => {
		if (show) clearPostApplyPending();
	}, [show]);

	if (!show) return null;

	const l = locale === "en" ? "en" : "id";
	return (
		<div
			data-testid="apply-ad-break"
			data-arm="ad_breaks"
			className="mt-4 space-y-3 rounded-2xl border border-border bg-card p-4 has-[[data-state=dismissed]]:hidden has-[[data-state=empty]]:hidden has-[[data-state=house]]:hidden"
		>
			<p role="status" className="text-sm font-semibold text-foreground">
				{CONFIRMATION[l]}
			</p>
			<AdBreakCard jobId={jobId} categoryId={categoryId} locale={locale} />
		</div>
	);
}
